import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  AlertTriangle, CheckCircle2, XCircle, Phone, Mail, ExternalLink,
  Building2, Shield, DollarSign, Cable, BarChart3, TrendingUp,
  Loader2, ChevronRight, ArrowRight, Activity, Bell, Send,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════
// 1. Quick Status Dashboard
// ═══════════════════════════════════════════
export function MobileStatusDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProp, setExpandedProp] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const load = async () => {
      try {
        const [propsRes, alertsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/properties?show_all=true`, { headers }),
          axios.get(`${BACKEND_URL}/api/reports/spend-anomalies`, { headers }).catch(() => ({ data: { alerts: [] } })),
        ]);
        const props = Array.isArray(propsRes.data) ? propsRes.data : propsRes.data.properties || [];
        setData({ properties: props, alerts: alertsRes.data?.alerts || [] });
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const statusColor = (count) => count === 0 ? 'bg-emerald-500' : count <= 2 ? 'bg-amber-400' : 'bg-red-500';

  return (
    <div className="pb-mobile-nav" data-testid="mobile-status-dashboard">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>System Status</h2>
        <p className="text-xs text-muted-foreground">{data?.properties?.length || 0} properties monitored</p>
      </div>
      {(data?.alerts || []).length > 0 && (
        <div className="px-4 pb-3">
          <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200">
            <div className="flex items-center gap-2 mb-1">
              <Bell className="h-4 w-4 text-red-600" />
              <span className="text-sm font-bold text-red-700">{data.alerts.length} Active Alerts</span>
            </div>
            {data.alerts.slice(0, 3).map((a, i) => (
              <p key={i} className="text-xs text-red-600 mt-1">{a.title}</p>
            ))}
          </div>
        </div>
      )}
      <ScrollArea className="flex-1">
        <div className="px-4 space-y-2 pb-4">
          {(data?.properties || []).map(p => (
            <button key={p.id} onClick={() => setExpandedProp(expandedProp === p.id ? null : p.id)}
              className="w-full text-left p-3 rounded-xl border bg-card active:bg-muted/60 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${statusColor(0)} shrink-0`} />
                <span className="text-sm font-medium flex-1 truncate">{p.name}</span>
                <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${expandedProp === p.id ? 'rotate-90' : ''}`} />
              </div>
              {expandedProp === p.id && (
                <div className="mt-2 pt-2 border-t space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between"><span>Type</span><Badge variant="outline" className="text-[8px]">{p.type || 'hotel'}</Badge></div>
                  <div className="flex justify-between"><span>Status</span><Badge className="text-[8px] bg-emerald-100 text-emerald-700">Operational</Badge></div>
                </div>
              )}
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ═══════════════════════════════════════════
// 2. My Properties
// ═══════════════════════════════════════════
export function MobileMyProperties({ token }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/properties`, { headers })
      .then(r => setProperties(Array.isArray(r.data) ? r.data : r.data.properties || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  const loadProperty = async (propId) => {
    setSelected(propId);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/assignments/layers`, { headers, params: { property_id: propId } });
      setAssignments(res.data.assignments || []);
    } catch { setAssignments([]); }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="pb-mobile-nav" data-testid="mobile-my-properties">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>My Properties</h2>
        <p className="text-xs text-muted-foreground">{properties.length} properties in your portfolio</p>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-4 space-y-2 pb-4">
          {!selected ? properties.map(p => (
            <button key={p.id} onClick={() => loadProperty(p.id)}
              className="w-full text-left p-4 rounded-xl border bg-card active:bg-muted/60 transition-colors">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium block truncate">{p.name}</span>
                  <span className="text-[10px] text-muted-foreground">{p.type}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          )) : (
            <div>
              <button onClick={() => setSelected(null)} className="text-xs text-primary flex items-center gap-1 mb-3">
                <ArrowRight className="h-3 w-3 rotate-180" /> Back to properties
              </button>
              <h3 className="text-sm font-bold mb-2">{properties.find(p => p.id === selected)?.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">{assignments.length} platforms deployed</p>
              {assignments.filter(a => a.layer === 'actual' || !a.layer).map((a, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/40 text-xs mb-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                  <span className="font-medium flex-1">{a.product_name || a.vendor_name || a.platform_type_id}</span>
                  <Badge variant="outline" className="text-[8px]">{a.platform_type_id}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ═══════════════════════════════════════════
// 3. Support Speed Dial
// ═══════════════════════════════════════════
export function MobileSupportDial({ token }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/support-contacts`, { headers });
        setContacts(res.data.contacts || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [headers]);

  const filtered = filter ? contacts.filter(c =>
    (c.platform_name || '').toLowerCase().includes(filter.toLowerCase()) ||
    (c.vendor_name || '').toLowerCase().includes(filter.toLowerCase())
  ) : contacts;

  return (
    <div className="pb-mobile-nav" data-testid="mobile-support-dial">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Support Speed Dial</h2>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search vendor or platform..."
          className="w-full mt-2 rounded-lg border bg-background px-3 py-2 text-sm" />
      </div>
      <ScrollArea className="flex-1">
        <div className="px-4 space-y-2 pb-4">
          {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto mt-8" /> :
          filtered.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No support contacts found. Add them in Admin.</p> :
          filtered.map((c, i) => (
            <div key={i} className="p-3 rounded-xl border bg-card space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold flex-1">{c.platform_name || c.vendor_name || 'Support'}</span>
                <Badge variant="outline" className="text-[8px]">{c.vendor_name}</Badge>
              </div>
              <div className="flex gap-2">
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="flex-1 flex items-center justify-center gap-1.5 p-2.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium active:bg-emerald-100">
                    <Phone className="h-4 w-4" /> Call
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="flex-1 flex items-center justify-center gap-1.5 p-2.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium active:bg-blue-100">
                    <Mail className="h-4 w-4" /> Email
                  </a>
                )}
                {c.portal_url && (
                  <a href={c.portal_url} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1.5 p-2.5 rounded-lg bg-purple-50 text-purple-700 text-xs font-medium active:bg-purple-100">
                    <ExternalLink className="h-4 w-4" /> Portal
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ═══════════════════════════════════════════
// 4. Incident Reporter
// ═══════════════════════════════════════════
export function MobileIncidentReporter({ token }) {
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [form, setForm] = useState({ property_id: '', platform_type_id: '', severity: 'medium', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      axios.get(`${BACKEND_URL}/api/stack`),
    ]).then(([pRes, sRes]) => {
      setProperties(Array.isArray(pRes.data) ? pRes.data : pRes.data.properties || []);
      setPlatforms((sRes.data.nodes || []).map(n => ({ id: n.id, name: n.short || n.name })));
    }).catch(() => {});
  }, [headers]);

  const submit = async () => {
    if (!form.property_id || !form.platform_type_id) { toast.error('Select property and platform'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/incidents`, {
        ...form, status: 'open', reported_at: new Date().toISOString(),
      }, { headers });
      toast.success('Incident reported');
      setSubmitted(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="flex flex-col items-center justify-center p-12 text-center pb-mobile-nav">
      <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-4" />
      <h3 className="text-lg font-bold mb-2">Incident Reported</h3>
      <p className="text-sm text-muted-foreground mb-6">Your team has been notified.</p>
      <Button onClick={() => { setSubmitted(false); setForm({ property_id: '', platform_type_id: '', severity: 'medium', description: '' }); }}>Report Another</Button>
    </div>
  );

  return (
    <div className="pb-mobile-nav" data-testid="mobile-incident-reporter">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Report Incident</h2>
        <p className="text-xs text-muted-foreground">Log a system outage or issue</p>
      </div>
      <div className="px-4 space-y-4 pb-4">
        <div>
          <label className="text-xs font-medium block mb-1">Property</label>
          <select value={form.property_id} onChange={e => setForm(f => ({ ...f, property_id: e.target.value }))}
            className="w-full h-11 rounded-lg border bg-background px-3 text-sm">
            <option value="">Select property...</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Platform</label>
          <select value={form.platform_type_id} onChange={e => setForm(f => ({ ...f, platform_type_id: e.target.value }))}
            className="w-full h-11 rounded-lg border bg-background px-3 text-sm">
            <option value="">Select platform...</option>
            {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Severity</label>
          <div className="grid grid-cols-3 gap-2">
            {[{ v: 'critical', l: 'Critical', c: 'bg-red-100 text-red-700 border-red-300' },
              { v: 'medium', l: 'Medium', c: 'bg-amber-100 text-amber-700 border-amber-300' },
              { v: 'low', l: 'Low', c: 'bg-blue-100 text-blue-700 border-blue-300' }].map(s => (
              <button key={s.v} onClick={() => setForm(f => ({ ...f, severity: s.v }))}
                className={`p-2.5 rounded-lg text-xs font-medium border text-center ${form.severity === s.v ? s.c + ' ring-2 ring-offset-1' : 'bg-muted/30 text-muted-foreground'}`}
                style={form.severity === s.v ? { '--tw-ring-color': s.v === 'critical' ? '#ef4444' : s.v === 'medium' ? '#f59e0b' : '#3b82f6' } : {}}>
                {s.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">What happened?</label>
          <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={3} placeholder="Describe the issue..." className="text-sm" />
        </div>
        <Button onClick={submit} disabled={submitting} className="w-full h-12 text-sm gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? 'Submitting...' : 'Report Incident'}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// 5. Portfolio Scorecard (Executive)
// ═══════════════════════════════════════════
export function MobilePortfolioScorecard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const load = async () => {
      try {
        const [healthRes, anomRes, spendRes, trendRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/reports/financial-health`, { headers }).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/reports/spend-anomalies`, { headers }).catch(() => ({ data: { summary: {} } })),
          axios.get(`${BACKEND_URL}/api/reports/vendor-spend-summary`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${BACKEND_URL}/api/reports/vendor-spend-trends`, { headers }).catch(() => ({ data: { vendors: [] } })),
        ]);
        setData({
          health: healthRes.data,
          anomalies: anomRes.data?.summary || {},
          spend: spendRes.data,
          trends: trendRes.data?.vendors?.slice(0, 5) || [],
        });
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const healthScore = data?.health?.summary?.avg_score || 0;
  const scoreColor = healthScore >= 70 ? '#16a34a' : healthScore >= 40 ? '#d97706' : '#dc2626';

  return (
    <div className="pb-mobile-nav" data-testid="mobile-portfolio-scorecard">
      <div className="px-4 pt-4 pb-3">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Portfolio Scorecard</h2>
      </div>
      <div className="px-4 space-y-4 pb-4">
        {/* Health Score */}
        <div className="p-5 rounded-2xl border bg-card text-center">
          <div className="text-5xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk', color: scoreColor }}>{healthScore}%</div>
          <div className="text-xs text-muted-foreground">Financial Health Score</div>
          <div className="w-full h-2 rounded-full bg-muted mt-3 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${healthScore}%`, backgroundColor: scoreColor }} />
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-xl border bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(data?.spend?.total_spend || 0).toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground">Total Spend</div>
          </div>
          <div className="p-4 rounded-xl border bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.spend?.vendor_count || 0}</div>
            <div className="text-[10px] text-muted-foreground">Active Vendors</div>
          </div>
          <div className="p-4 rounded-xl border bg-card text-center">
            <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{data?.anomalies?.total_alerts || 0}</div>
            <div className="text-[10px] text-muted-foreground">Active Alerts</div>
          </div>
          <div className="p-4 rounded-xl border bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.health?.summary?.properties_analyzed || 0}</div>
            <div className="text-[10px] text-muted-foreground">Properties</div>
          </div>
        </div>

        {/* Top Spend Trends */}
        {(data?.trends || []).length > 0 && (
          <div className="p-4 rounded-xl border bg-card">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Top Vendor Spend</h3>
            {data.trends.map((t, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 text-xs">
                <span className="text-muted-foreground w-4">#{i + 1}</span>
                <span className="font-medium flex-1 truncate">{t.vendor_name}</span>
                <Badge variant="outline" className={`text-[8px] ${t.trend === 'up' ? 'text-red-600' : t.trend === 'down' ? 'text-emerald-600' : ''}`}>
                  {t.trend === 'up' ? '↑' : t.trend === 'down' ? '↓' : '→'}
                </Badge>
                <span className="font-bold tabular-nums">${t.total.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// 6. Vendor Spend Rings
// ═══════════════════════════════════════════
export function MobileSpendRings({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/vendor-spend-summary`, { headers })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const vendors = data?.vendors || [];
  const total = data?.total_spend || 1;
  const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#d8b4fe', '#e9d5ff', '#f3e8ff', '#7c3aed', '#4f46e5', '#312e81'];

  // Simple donut chart via SVG
  let cumAngle = 0;
  const slices = vendors.slice(0, 8).map((v, i) => {
    const pct = v.total_spend / total;
    const startAngle = cumAngle;
    cumAngle += pct * 360;
    return { ...v, pct, startAngle, endAngle: cumAngle, color: colors[i % colors.length] };
  });

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const rad = (angleDeg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const arcPath = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y}`;
  };

  return (
    <div className="pb-mobile-nav" data-testid="mobile-spend-rings">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Vendor Spend</h2>
        <p className="text-xs text-muted-foreground">${total.toLocaleString()} across {vendors.length} vendors</p>
      </div>
      <div className="flex justify-center py-4">
        <svg width="200" height="200" viewBox="0 0 200 200">
          {slices.map((s, i) => (
            <path key={i} d={arcPath(100, 100, 70, s.startAngle, s.endAngle - 0.5)}
              fill="none" stroke={s.color} strokeWidth="24" strokeLinecap="round"
              onClick={() => setExpanded(expanded === i ? null : i)}
              style={{ cursor: 'pointer', opacity: expanded !== null && expanded !== i ? 0.3 : 1 }} />
          ))}
          <text x="100" y="95" textAnchor="middle" className="text-lg font-bold fill-foreground" style={{ fontFamily: 'Space Grotesk' }}>
            {expanded !== null ? `${Math.round(slices[expanded]?.pct * 100)}%` : vendors.length}
          </text>
          <text x="100" y="112" textAnchor="middle" className="text-[10px] fill-muted-foreground">
            {expanded !== null ? slices[expanded]?.vendor_name?.split(' ')[0] : 'vendors'}
          </text>
        </svg>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-4 space-y-1.5 pb-4">
          {slices.map((s, i) => (
            <button key={i} onClick={() => setExpanded(expanded === i ? null : i)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl text-xs transition-colors ${expanded === i ? 'bg-muted/60 ring-1 ring-primary/20' : 'active:bg-muted/40'}`}>
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
              <span className="font-medium flex-1 text-left">{s.vendor_name}</span>
              <span className="text-muted-foreground">{Math.round(s.pct * 100)}%</span>
              <span className="font-bold tabular-nums">${s.total_spend.toLocaleString()}</span>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ═══════════════════════════════════════════
// 7. Integration Health
// ═══════════════════════════════════════════
export function MobileIntegrationHealth({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/integration-gaps`, { headers })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const properties = (data?.properties || []).filter(p => p.total_template > 0);

  const statusColor = (p) => {
    if (p.gaps === 0 && p.live > 0) return 'bg-emerald-500';
    if (p.gaps > p.live) return 'bg-red-500';
    return 'bg-amber-400';
  };

  const statusLabel = (p) => {
    if (p.gaps === 0 && p.live > 0) return 'Healthy';
    if (p.gaps > p.live) return 'At Risk';
    return 'Partial';
  };

  return (
    <div className="pb-mobile-nav" data-testid="mobile-integration-health">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Integration Health</h2>
        <p className="text-xs text-muted-foreground">{properties.length} properties with integration blueprints</p>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-4 space-y-2 pb-4">
          {properties.length === 0 ? (
            <div className="text-center py-12">
              <Cable className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No integration data yet. Define product-level integrations to see health.</p>
            </div>
          ) : properties.map(p => (
            <div key={p.property_id} className="p-3 rounded-xl border bg-card">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${statusColor(p)} shrink-0`} />
                <span className="text-sm font-medium flex-1 truncate">{p.property_name}</span>
                <Badge variant="outline" className={`text-[8px] ${p.gaps > p.live ? 'text-red-600 border-red-300' : 'text-emerald-600 border-emerald-300'}`}>{statusLabel(p)}</Badge>
              </div>
              <div className="flex gap-3 text-[10px]">
                <span className="text-emerald-600 font-medium">{p.live ?? p.active ?? 0} live</span>
                <span className="text-blue-600">{p.available ?? p.possible ?? 0} available</span>
                <span className="text-red-500">{p.gaps} gaps</span>
                <span className="text-muted-foreground ml-auto">{p.coverage}% coverage</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${p.coverage}%` }} />
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
